// src/components/ProductionDashboard/QCDashboard.js
// Task 15 — QC Dashboard with real analytics
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, ShieldCheck, CheckCircle2, XCircle, AlertTriangle,
    Clock, BarChart3, Users, RefreshCcw,
    Gem, Layers, Menu
} from 'lucide-react';
import { productionApi } from '../api';
import SkeletonLoader from './components/SkeletonLoader';
import ApiButton from './components/ApiButton';
import { BREAKPOINTS } from './productionTheme';

// ========== KPI Card ==========
const KPICard = ({ label, value, icon: Icon, color, bg, sub }) => (
    <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bg} ${color}`}>
            <Icon size={22} />
        </div>
        <div>
            <div className="text-2xl font-black text-slate-900">{value}</div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</div>
            {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    </div>
);

// ========== Pass/Fail Trend Chart (CSS bars) ==========
const PassFailChart = ({ data }) => {
    if (!data || data.length === 0) {
        return <div className="text-center text-slate-400 py-8 text-sm">No trend data available</div>;
    }
    const maxTotal = Math.max(...data.map(d => d.pass + d.fail), 1);

    return (
        <div className="space-y-1">
            <div className="flex items-end gap-1 h-40">
                {data.map((d, i) => {
                    const passH = (d.pass / maxTotal) * 100;
                    const failH = (d.fail / maxTotal) * 100;
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                            <div className="w-full flex flex-col items-stretch justify-end h-full gap-px">
                                {d.fail > 0 && (
                                    <div className="bg-rose-400 rounded-t-sm transition-all duration-500" style={{ height: `${failH}%`, minHeight: d.fail ? '2px' : 0 }} />
                                )}
                                {d.pass > 0 && (
                                    <div className="bg-emerald-400 rounded-t-sm transition-all duration-500" style={{ height: `${passH}%`, minHeight: d.pass ? '2px' : 0 }} />
                                )}
                            </div>
                            {/* Tooltip */}
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                {d.date?.slice(5)}: ✓{d.pass} ✗{d.fail}
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* X-axis labels — show every 5th */}
            <div className="flex gap-1">
                {data.map((d, i) => (
                    <div key={i} className="flex-1 text-center text-[8px] text-slate-400 font-mono">
                        {i % 5 === 0 ? d.date?.slice(5) : ''}
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-4 mt-2 text-[10px] font-bold text-slate-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-400 inline-block" /> Pass</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-rose-400 inline-block" /> Fail</span>
            </div>
        </div>
    );
};

// ========== Rework Aging Buckets ==========
const ReworkAging = ({ data }) => {
    if (!data?.buckets) return null;
    const colors = [
        { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
        { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
        { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
        { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700' },
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.buckets.map((b, i) => {
                const c = colors[i] || colors[3];
                return (
                    <div key={i} className={`p-4 rounded-xl border ${c.bg} ${c.border} text-center`}>
                        <div className={`text-2xl font-black ${c.text}`}>{b.count}</div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1">{b.label}</div>
                    </div>
                );
            })}
        </div>
    );
};

// ========== Inspector Performance Table ==========
const InspectorTable = ({ data }) => {
    if (!data || data.length === 0) {
        return <div className="text-center text-slate-400 py-8 text-sm">No inspector data available</div>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="text-left px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Inspector</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Total</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Approved</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Rejected</th>
                        <th className="text-right px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Pass Rate</th>
                        <th className="text-center px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Performance</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {data.map((r, i) => {
                        const isGood = r.pass_rate >= 80;
                        const isMedium = r.pass_rate >= 60 && r.pass_rate < 80;
                        return (
                            <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                                <td className="px-4 py-3 font-bold text-slate-700">{r.inspector || 'Unknown'}</td>
                                <td className="px-4 py-3 text-right font-mono text-slate-600">{r.total_reviewed}</td>
                                <td className="px-4 py-3 text-right font-mono text-emerald-600">{r.approved}</td>
                                <td className="px-4 py-3 text-right font-mono text-rose-600">{r.rejected}</td>
                                <td className="px-4 py-3 text-right">
                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-black ${isGood ? 'bg-emerald-100 text-emerald-700' : isMedium ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                                        {r.pass_rate}%
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <div className="w-20 mx-auto">
                                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                                            <div className={`h-full rounded-full ${isGood ? 'bg-emerald-500' : isMedium ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${r.pass_rate}%` }} />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// ========== MAIN QC DASHBOARD ==========
export default function QCDashboard() {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [passFail, setPassFail] = useState([]);
    const [inspectors, setInspectors] = useState([]);
    const [reworkData, setReworkData] = useState(null);
    const [stageDistribution, setStageDistribution] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const res = await productionApi.get('/production/qc-stats');
            if (res.data?.success) {
                setSummary(res.data.summary || null);
                setStageDistribution(res.data.stage_distribution || []);
                setPassFail(res.data.pass_fail_trend || []);
                setInspectors(res.data.inspector_performance || []);
                setReworkData(res.data.rework_aging || null);
            }
        } catch (e) {
            console.error('QC dashboard load error:', e);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadAll();
        const interval = setInterval(loadAll, 30000); // refresh every 30s
        return () => clearInterval(interval);
    }, [loadAll]);

    return (
        <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
            <style>{`
                @media (max-width: ${BREAKPOINTS.mobile}) {
                    .qc-sidebar { position: fixed; inset: 0 auto 0 0; width: 250px; transform: translateX(-100%); transition: transform .25s ease; z-index: 40; }
                    .qc-sidebar.open { transform: translateX(0); }
                    .qc-overlay { display: block; }
                    .qc-content { padding: 16px !important; }
                    .qc-kpi-grid { grid-template-columns: 1fr !important; }
                }
                @media (min-width: ${BREAKPOINTS.mobile}) and (max-width: ${BREAKPOINTS.desktop}) {
                    .qc-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
                }
            `}</style>
            {sidebarOpen && <div className="qc-overlay fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}
            {/* Sidebar */}
            <aside className={`qc-sidebar w-64 bg-gradient-to-b from-slate-800 via-slate-900 to-slate-900 text-white flex flex-col shadow-2xl z-20 ${sidebarOpen ? 'open' : ''}`}>
                <div className="p-6 border-b border-white/10 flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Gem size={24} />
                    </div>
                    <span className="text-xl font-black tracking-tighter uppercase italic">
                        ZILVERRA<span className="text-indigo-400">JEWELS</span>
                    </span>
                </div>
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    <div className="text-xs font-bold text-slate-400 uppercase px-3 mt-2 mb-2">QC Module</div>
                    <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 text-white">
                        <ShieldCheck size={20} className="text-indigo-400" /> QC Dashboard
                    </div>
                    <button onClick={() => navigate('/production/final-qc')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 transition-all">
                        <CheckCircle2 size={20} /> Final QC & Packing
                    </button>
                    <div className="text-xs font-bold text-slate-400 uppercase px-3 mt-6 mb-2">Nav</div>
                    <button onClick={() => navigate('/production')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 transition-all">
                        <ArrowLeft size={20} /> Back to Dashboard
                    </button>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50/50">
                <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 shadow-sm z-10">
                    <div className="flex items-center gap-2 text-sm">
                        <button className="md:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setSidebarOpen(true)}>
                            <Menu size={18} />
                        </button>
                        <span onClick={() => navigate('/production')} className="text-slate-500 hover:text-indigo-600 cursor-pointer transition-colors">Home</span>
                        <span className="text-slate-300">/</span>
                        <span className="font-medium text-slate-800">QC Dashboard</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <ApiButton loading={loading} onClick={loadAll} className="rounded-xl text-xs px-4 py-2">
                            <RefreshCcw size={14} />
                            {loading ? 'Loading...' : 'Refresh'}
                        </ApiButton>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto">
                    <div className="qc-content max-w-6xl mx-auto p-8 space-y-8">
                        {/* Title */}
                        <div>
                            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                                <ShieldCheck className="text-indigo-500" size={28} /> Quality Control Dashboard
                            </h1>
                            <p className="text-sm text-slate-500 mt-1">Live QC analytics with 30-second auto-refresh</p>
                        </div>

                        {/* KPI Banner */}
                        <div className="qc-kpi-grid grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard label="Pending QC" value={summary?.pending_qc ?? '—'} icon={Clock} color="text-amber-600" bg="bg-amber-50" />
                            <KPICard label="Approved Today" value={summary?.approved_today ?? '—'} icon={CheckCircle2} color="text-emerald-600" bg="bg-emerald-50" />
                            <KPICard label="Rejected Today" value={summary?.rejected_today ?? '—'} icon={XCircle} color="text-rose-600" bg="bg-rose-50" />
                            <KPICard label="Open Reworks" value={summary?.rework_pending ?? '—'} icon={AlertTriangle} color="text-orange-600" bg="bg-orange-50" />
                        </div>

                        {/* Pass/Fail Trend + Rework Aging */}
                        {loading ? (
                            <SkeletonLoader count={4} height={180} />
                        ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Pass/Fail Trend */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                                    <BarChart3 size={16} className="text-indigo-400" /> Pass/Fail Trend (30 Days)
                                </h2>
                                <PassFailChart data={passFail} />
                            </div>

                            {/* Rework Aging */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                                    <AlertTriangle size={16} className="text-orange-400" /> Rework Aging
                                    {reworkData?.total_open > 0 && (
                                        <span className="ml-auto text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{reworkData.total_open} open</span>
                                    )}
                                </h2>
                                <ReworkAging data={reworkData} />
                                {reworkData?.critical?.length > 0 && (
                                    <div className="mt-4 p-3 bg-rose-50 rounded-xl border border-rose-200">
                                        <div className="text-xs font-bold text-rose-700 mb-1">⚠️ Critical ({'>'}7 days)</div>
                                        {reworkData.critical.slice(0, 5).map((c, i) => (
                                            <div key={i} className="text-xs text-rose-600 py-0.5">
                                                Rework #{c.rework_id} — {c.age_days} days old — Job #{c.job_card_id}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        )}

                        {/* QC Queue Heatmap by Stage */}
                        {stageDistribution.length > 0 && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                                    <Layers size={16} className="text-blue-400" /> QC Queue by Stage
                                </h2>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                    {stageDistribution.map((s, i) => {
                                        const bg = s.count > 5 ? 'bg-rose-50 border-rose-200' : s.count > 2 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
                                        const textColor = s.count > 5 ? 'text-rose-700' : s.count > 2 ? 'text-amber-700' : 'text-emerald-700';
                                        return (
                                            <div key={i} className={`p-3 rounded-xl border ${bg} text-center transition-all hover:scale-105`}>
                                                <div className={`text-xl font-black ${textColor}`}>{s.count}</div>
                                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-0.5 truncate">{s.stage}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Inspector Performance Table */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                    <Users size={16} className="text-indigo-400" /> Inspector Performance
                                </h2>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    {inspectors.length} inspectors
                                </span>
                            </div>
                            <InspectorTable data={inspectors} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
